import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { Logger } from '../utils/logger';
import error500 from 'assets/images/maintenance/Error500.png';
import config from 'config';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const REACT_ERROR_DESCRIPTIONS: Record<number, string> = {
  130: 'Element type is invalid',
  152: 'Nothing was returned from render',
  185: 'Maximum update depth exceeded',
  300: 'Cannot update a component while rendering a different component (setState called during render)',
  301: 'Cannot update during an existing state transition',
  310: 'Objects are not valid as a React child',
  321: 'useContext hook was called outside of a function component',
  394: 'Expected useEffect callback to return nothing or a cleanup function'
};

function decodeReactMinifiedError(message: string): string | null {
  const match = message.match(/Minified React error #(\d+)/);
  if (match) {
    const errorCode = parseInt(match[1], 10);
    return REACT_ERROR_DESCRIPTIONS[errorCode] || `React error #${errorCode}`;
  }
  return null;
}

function extractComponentFromStack(componentStack: string | undefined): string[] {
  if (!componentStack) return [];
  const componentMatches = componentStack.match(/at (\w+)/g);
  if (!componentMatches) return [];
  return componentMatches
    .map(m => m.replace('at ', ''))
    .filter(name => /^[A-Z]/.test(name) && name.length > 2)
    .slice(0, 5);
}

const logger = new Logger('ErrorBoundary');

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const decodedError = decodeReactMinifiedError(error.message);
    const likelyComponents = extractComponentFromStack(errorInfo.componentStack || undefined);
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : 'unknown';

    logger.error('React component error', error, {
      decodedError,
      likelyComponents,
      currentPath,
      componentStack: errorInfo.componentStack,
      errorMessage: error.message,
      errorName: error.name,
      errorStack: error.stack
    });
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = (): void => {
    window.location.href = config.defaultPath;
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Grid container direction="column" alignItems="center" justifyContent="center" sx={{ minHeight: '90vh' }}>
          <Grid item xs={12}>
            <Box sx={{ width: { xs: 350, sm: 396 } }}>
              <img src={error500} alt="error" style={{ height: '100%', width: '100%' }} />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Stack justifyContent="center" alignItems="center">
              <Typography align="center" variant="h1">
                Something went wrong
              </Typography>
              <Typography color="textSecondary" variant="body2" align="center" sx={{ width: { xs: '73%', sm: '70%' }, mt: 1 }}>
                An unexpected error occurred. Please try again or contact support if the problem persists.
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                <Button variant="contained" onClick={this.handleRetry} sx={{ textTransform: 'none' }}>
                  Try again
                </Button>
                <Button variant="outlined" onClick={this.handleGoHome} sx={{ textTransform: 'none' }}>
                  Go to homepage
                </Button>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
